/**
 * Copyright 2020 Amazon.com, Inc. or its affiliates. All Rights Reserved.
 * SPDX-License-Identifier: LicenseRef-.amazon.com.-AmznSL-1.0
 * Licensed under the Amazon Software License  http://aws.amazon.com/asl/
 */

// add 2359 - add support for EMR-hail notebook entities
import _ from 'lodash';
import { types } from 'mobx-state-tree';
import { getMyNotebooks, getOrganizationNotebooks, getAccessibleNotebooks } from '../../helpers/api';
import { consolidateToMap } from '../../helpers/utils';
import { BaseStore } from '../BaseStore';
import { Notebook } from './Notebook';

const apiLoadMethods = { getMyNotebooks, getOrganizationNotebooks, getAccessibleNotebooks };

// ==================================================================
// NotebooksStore
// ==================================================================
const NotebooksStore = BaseStore.named('NotebooksStore')
  .props({
    category: '',
    notebooks: types.optional(types.map(Notebook), {}),
    tickPeriod: 900 * 1000, // 15 minutes
    loadMethod: types.maybeNull(types.string, 'getMyNotebooks'),
  })

  .actions(self => {
    // save the base implementation of cleanup
    const superCleanup = self.cleanup;

    return {
      async doLoad() {
        let notebooks;
        if (self.loadMethod) {
          notebooks = await apiLoadMethods[self.loadMethod]();
        } else {
          notebooks = await getMyNotebooks(self.category);
        }
        self.runInAction(() => {
          consolidateToMap(self.notebooks, notebooks, (exiting, newItem) => {
            exiting.setNotebook(newItem);
          });
        });
      },

      cleanup: () => {
        self.notebooks.clear();
        superCleanup();
      },
    };
  })

  .views(self => ({
    get empty() {
      return self.notebooks.size === 0;
    },

    get total() {
      return self.notebooks.size;
    },

    get list() {
      const result = [];
      self.notebooks.forEach(notebook => result.push(notebook));

      return _.reverse(_.sortBy(result, ['createdAt', 'name']));
    },

    get numOfPages() {
      // TODO: implement pagination for when we have more data
      return 1;
    },

    hasNotebook(id) {
      return self.notebooks.has(id);
    },

    getNotebook(id) {
      return self.notebooks.get(id);
    },
  }));

function registerModels(globals) {
  globals.notebooksStore = NotebooksStore.create({}, globals);
}

export { NotebooksStore, registerModels };
